import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, apiMethods } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { useLanguageStore, Language } from '../store/languageStore';
import { UserSettings, IgnoredUser, GlobalEditor, GlobalEditorInvite } from '../types';
import { TrashIcon, PlusIcon, ExclamationTriangleIcon, UserPlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';
import Footer from '../components/Footer';
import Toggle from '../components/Toggle';
import toast from 'react-hot-toast';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const { language, setLanguage } = useLanguageStore();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [ignoredUsers, setIgnoredUsers] = useState<IgnoredUser[]>([]);
  const [globalEditors, setGlobalEditors] = useState<GlobalEditor[]>([]);
  const [globalInvites, setGlobalInvites] = useState<GlobalEditorInvite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newIgnoreUsername, setNewIgnoreUsername] = useState('');
  const [newIgnoreReason, setNewIgnoreReason] = useState('');
  const [newGlobalEditorUsername, setNewGlobalEditorUsername] = useState('');
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState(false);

  // Twitch user search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Ignored user search state
  const [ignoreSearchQuery, setIgnoreSearchQuery] = useState('');
  const [ignoreSearchResults, setIgnoreSearchResults] = useState<any[]>([]);
  const [isIgnoreSearching, setIsIgnoreSearching] = useState(false);
  const [showIgnoreSearchResults, setShowIgnoreSearchResults] = useState(false);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const ignoreSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper functions to handle defaultKeywords as JSON string
  const parseDefaultKeywords = (jsonString: string): string[] => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return [];
    }
  };

  const stringifyDefaultKeywords = (keywords: string[]): string => {
    return JSON.stringify(keywords);
  };

  const loadData = useCallback(async () => {
    try {
      const [settingsRes, ignoredRes, globalEditorsRes, globalInvitesRes] = await Promise.all([
        api.get('/api/settings'),
        api.get('/api/ignored-users'),
        api.get('/api/global-editors'),
        api.get('/api/global-editor-invites')
      ]);
      setSettings(settingsRes.data);
      setIgnoredUsers(ignoredRes.data);
      setGlobalEditors(globalEditorsRes.data);
      setGlobalInvites(globalInvitesRes.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error(t('settings.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle clicking outside search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.search-container') && !target.closest('.search-results-container')) {
        setShowSearchResults(false);
      }
      if (!target.closest('.ignore-search-container') && !target.closest('.search-results-container')) {
        setShowIgnoreSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const updateSettings = async (newSettings: Partial<UserSettings>) => {
    if (!settings) return;
    
    try {
      // If theme is being updated, apply it immediately via theme store
      if (newSettings.theme) {
        setTheme(newSettings.theme as any);
      }
      
      const updated = await apiMethods.updateSettings({ ...settings, ...newSettings });
      setSettings(updated);
      toast.success(t('settings.updateSuccess'));
    } catch (error) {
      toast.error(t('settings.updateFailed'));
    }
  };

  const addIgnoredUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newIgnoreUsername.trim()) return;

    try {
      const response = await api.post('/api/ignored-users', {
        username: newIgnoreUsername,
        reason: newIgnoreReason || undefined
      });
      setIgnoredUsers([response.data, ...ignoredUsers]);
      setNewIgnoreUsername('');
      setNewIgnoreReason('');
      setIgnoreSearchQuery('');
      setIgnoreSearchResults([]);
      setShowIgnoreSearchResults(false);
      toast.success(t('settings.addIgnoredUserSuccess'));
    } catch (error: any) {
      console.error('Error adding ignored user:', error);
      toast.error(error.response?.data?.error || t('settings.addIgnoredUserFailed'));
    }
  };

  const removeIgnoredUser = async (id: string) => {
    try {
      await api.delete(`/api/ignored-users/${id}`);
      setIgnoredUsers(ignoredUsers.filter(user => user.id !== id));
      toast.success(t('settings.removeIgnoredUserSuccess'));
    } catch (error) {
      console.error('Error removing ignored user:', error);
      toast.error(t('settings.removeIgnoredUserFailed'));
    }
  };

  const addGlobalEditor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGlobalEditorUsername.trim()) return;

    try {
      await api.post('/api/global-editors/invite', {
        username: newGlobalEditorUsername,
        role: 'editor'
      });
      toast.success(t('settings.addGlobalEditorSuccess'));
      setNewGlobalEditorUsername('');
      loadData(); // Refresh data
    } catch (error: any) {
      console.error('Error inviting global editor:', error);
      toast.error(error.response?.data?.error || t('settings.addGlobalEditorFailed'));
    }
  };

  const removeGlobalEditor = async (id: string) => {
    try {
      await api.delete(`/api/global-editors/${id}`);
      setGlobalEditors(globalEditors.filter(editor => editor.id !== id));
      toast.success(t('settings.removeGlobalEditorSuccess'));
    } catch (error) {
      console.error('Error removing global editor:', error);
      toast.error(t('settings.removeGlobalEditorFailed'));
    }
  };

  const acceptGlobalEditorInvite = async (inviteId: string) => {
    try {
      await api.put(`/api/global-editor-invites/${inviteId}`, { status: 'accepted' });
      toast.success(t('settings.acceptInviteSuccess'));
      loadData(); // Refresh data to update the lists
    } catch (error) {
      console.error('Error accepting invite:', error);
      toast.error(t('settings.acceptInviteFailed'));
    }
  };

  const rejectGlobalEditorInvite = async (inviteId: string) => {
    try {
      await api.put(`/api/global-editor-invites/${inviteId}`, { status: 'declined' });
      toast.success(t('settings.rejectInviteSuccess'));
      loadData(); // Refresh data to update the lists
    } catch (error) {
      console.error('Error rejecting invite:', error);
      toast.error(t('settings.rejectInviteFailed'));
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const result = await apiMethods.deleteAccount();
      
      toast.success(t('settings.deleteAccountSuccess', {
        sessions: result.deletedData.sessions,
        chatters: result.deletedData.chatters
      }));
      
      // Log out and redirect to home
      logout();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(t('settings.deleteAccountFailed'));
    } finally {
      setDeleteAccountConfirm(false);
    }
  };

  // Twitch user search functions
  const searchTwitchUsers = async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.get(`/api/twitch/search-user?username=${encodeURIComponent(query)}`);
      const result = response.data;
      
      if (result.found) {
        if (result.inDatabase) {
          // User exists in database
          setSearchResults([{
            ...result.user,
            type: 'database',
            status: result.user.isApproved ? 'Approved' : 'Pending Approval'
          }]);
        } else {
          // User found on Twitch but not in database
          setSearchResults([{
            ...result.twitchUser,
            type: 'twitch',
            status: 'Not in Database'
          }]);
        }
      } else {
        setSearchResults([]);
      }
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching for user:', error);
      toast.error(t('settings.searchUserFailed'));
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const importTwitchUser = async (username: string) => {

    try {
      const response = await api.post('/api/twitch/import-user', { username });

      toast.success(t('settings.globalEditors.importSuccess', { displayName: response.data.user.displayName }));
      
      // Update search results to show the imported user
      setSearchResults([{
        ...response.data.user,
        type: 'database',
        status: response.data.user.isApproved ? 'Approved' : 'Pending Approval'
      }]);
      
      setSearchQuery(username);
      // Keep dropdown open to show the updated result
      setShowSearchResults(true);
    } catch (error: any) {
      console.error('Error importing user:', error);
      if (error.response?.data?.error === 'User already exists in database') {
        toast.error(t('settings.globalEditors.userExists'));
        // Update search results to show the existing user
        setSearchResults([{
          ...error.response.data.user,
          type: 'database',
          status: error.response.data.user.isApproved ? 'Approved' : 'Pending Approval'
        }]);
        setShowSearchResults(true);
      } else {
        toast.error(t('settings.globalEditors.importFailed'));
      }
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchTwitchUsers(query);
    }, 300);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchResultClick = (result: any) => {
    
    if (result.type === 'twitch') {
      
      // Import the user from Twitch
      importTwitchUser(result.username);
      // Don't close the dropdown immediately - let the import function handle it
    } else {
      
      // User is already in database, use their username for the invite
      setNewGlobalEditorUsername(result.username);
      setShowSearchResults(false);
    }
  };

  const handleSearchInputFocus = () => {
    if (searchResults.length > 0) {
      setShowSearchResults(true);
    }
  };

  const handleSearchInputBlur = (e: React.FocusEvent) => {
    // Don't close dropdown immediately - let clicks register first
    setTimeout(() => {
      // Check if the new focus target is within the search results
      const relatedTarget = e.relatedTarget as HTMLElement;
      if (!relatedTarget || !relatedTarget.closest('.search-results-container')) {
        setShowSearchResults(false);
      }
    }, 200);
  };

  const handleSearchResultMouseDown = (e: React.MouseEvent) => {
    // Prevent the input from losing focus when clicking on search results
    e.preventDefault();
  };

  const handleImportButtonMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Ignored user search functions
  const searchIgnoredUsers = async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setIgnoreSearchResults([]);
      setShowIgnoreSearchResults(false);
      return;
    }

    setIsIgnoreSearching(true);
    try {
      const response = await api.get(`/api/twitch/search-user?username=${encodeURIComponent(query)}`);
      const result = response.data;
      
      if (result.found) {
        setIgnoreSearchResults([{
          ...result.inDatabase ? result.user : result.twitchUser,
          type: result.inDatabase ? 'database' : 'twitch'
        }]);
      } else {
        setIgnoreSearchResults([]);
      }
      setShowIgnoreSearchResults(true);
    } catch (error) {
      console.error('Error searching for ignored user:', error);
      toast.error(t('settings.searchUserFailed'));
      setIgnoreSearchResults([]);
    } finally {
      setIsIgnoreSearching(false);
    }
  };

  const handleIgnoreSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setIgnoreSearchQuery(query);
    
    // Clear previous timeout
    if (ignoreSearchTimeoutRef.current) {
      clearTimeout(ignoreSearchTimeoutRef.current);
    }
    
    // Debounce search
    ignoreSearchTimeoutRef.current = setTimeout(() => {
      searchIgnoredUsers(query);
    }, 300);
  };

  const handleIgnoreSearchResultClick = (result: any) => {

    setNewIgnoreUsername(result.username);
    setIgnoreSearchQuery(result.displayName);
    setShowIgnoreSearchResults(false);
  };

  const handleIgnoreSearchInputFocus = () => {
    if (ignoreSearchResults.length > 0) {
      setShowIgnoreSearchResults(true);
    }
  };

  const handleIgnoreSearchInputBlur = (e: React.FocusEvent) => {
    // Don't close dropdown immediately - let clicks register first
    setTimeout(() => {
      // Check if the new focus target is within the search results
      const relatedTarget = e.relatedTarget as HTMLElement;
      if (!relatedTarget || !relatedTarget.closest('.search-results-container')) {
        setShowIgnoreSearchResults(false);
      }
    }, 200);
  };

  const handleIgnoreSearchResultMouseDown = (e: React.MouseEvent) => {

    e.preventDefault();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white light:text-gray-900 mb-2">{t('settings.title')}</h1>
          <p className="text-gray-400 light:text-gray-600">{t('settings.subtitle')}</p>
        </div>

        <div className="space-y-8">
          {/* General Settings */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-6">{t('settings.sections.preferences.title')}</h2>
            
            {settings && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-medium text-white light:text-gray-900">Sound Notifications</h3>
                    <p className="text-sm text-gray-400 light:text-gray-600">Play a sound when new chatters arrive</p>
                  </div>
                  <Toggle
                    checked={settings.soundNotifications}
                    onChange={(checked) => updateSettings({ soundNotifications: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-medium text-white light:text-gray-900">Auto-start Sessions</h3>
                    <p className="text-sm text-gray-400 light:text-gray-600">Automatically start monitoring when you go live</p>
                  </div>
                  <Toggle
                    checked={settings.autoStartSessions}
                    onChange={(checked) => updateSettings({ autoStartSessions: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-medium text-white light:text-gray-900">OBS Mode</h3>
                    <p className="text-sm text-gray-400 light:text-gray-600">Optimize interface for OBS browser source</p>
                  </div>
                  <Toggle
                    checked={settings.obsMode}
                    onChange={(checked) => updateSettings({ obsMode: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-medium text-white light:text-gray-900">{t('settings.announceSessionStatus.label')}</h3>
                    <p className="text-sm text-gray-400 light:text-gray-600">{t('settings.announceSessionStatus.description')}</p>
                  </div>
                  <Toggle
                    checked={settings.announceSessionStatus}
                    onChange={(checked) => updateSettings({ announceSessionStatus: checked })}
                  />
                </div>

                <div>
                  <label className="block text-base font-medium text-white light:text-gray-900 mb-2">
                    {t('settings.sections.preferences.theme.label')}
                  </label>
                  <select
                    value={theme}
                    onChange={(e) => updateSettings({ theme: e.target.value })}
                    className="input w-full max-w-xs"
                  >
                    <option value="dark">{t('settings.sections.preferences.theme.options.dark')}</option>
                    <option value="light">{t('settings.sections.preferences.theme.options.light')}</option>
                  </select>
                  <p className="text-sm text-gray-400 light:text-gray-600 mt-1">
                    {t('settings.sections.preferences.theme.description')}
                  </p>
                </div>

                <div>
                  <label className="block text-base font-medium text-white light:text-gray-900 mb-2">
                    {t('settings.sections.preferences.language.label')}
                  </label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value as Language)}
                    className="input w-full max-w-xs"
                  >
                    <option value="en">{t('settings.sections.preferences.language.options.en')}</option>
                    <option value="de">{t('settings.sections.preferences.language.options.de')}</option>
                    <option value="es">{t('settings.sections.preferences.language.options.es')}</option>
                    <option value="fr">{t('settings.sections.preferences.language.options.fr')}</option>
                  </select>
                  <p className="text-sm text-gray-400 light:text-gray-600 mt-1">
                    {t('settings.sections.preferences.language.description')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Default Keywords */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-6">Default Keywords</h2>
            <p className="text-gray-400 light:text-gray-600 mb-4">
              Set default keywords that will be automatically applied to new sessions
            </p>
            
            {settings && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {parseDefaultKeywords(settings.defaultKeywords).map((keyword, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-twitch-600 text-white"
                    >
                      {keyword}
                      <button
                        onClick={() => {
                          const currentKeywords = parseDefaultKeywords(settings.defaultKeywords);
                          const newKeywords = currentKeywords.filter((_, i) => i !== index);
                          updateSettings({ defaultKeywords: stringifyDefaultKeywords(newKeywords) });
                        }}
                        className="ml-2 text-twitch-300 hover:text-white"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Add keyword"
                    className="input flex-1"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const input = e.target as HTMLInputElement;
                        const keyword = input.value.trim();
                        const currentKeywords = parseDefaultKeywords(settings.defaultKeywords);
                        if (keyword && !currentKeywords.includes(keyword)) {
                          updateSettings({ 
                            defaultKeywords: stringifyDefaultKeywords([...currentKeywords, keyword]) 
                          });
                          input.value = '';
                        }
                      }
                    }}
                  />
                  <button
                    onClick={(e) => {
                      const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                      const keyword = input.value.trim();
                      const currentKeywords = parseDefaultKeywords(settings.defaultKeywords);
                      if (keyword && !currentKeywords.includes(keyword)) {
                        updateSettings({ 
                          defaultKeywords: stringifyDefaultKeywords([...currentKeywords, keyword]) 
                        });
                        input.value = '';
                      }
                    }}
                    className="btn-secondary"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Ignored Users */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-6">{t('settings.ignoredUsers.title')}</h2>
            <p className="text-gray-400 light:text-gray-600 mb-4">
              {t('settings.ignoredUsers.description')}
            </p>

            {/* Add Ignored User Form */}
            <form onSubmit={addIgnoredUser} className="mb-6 p-4 bg-gray-700 rounded-lg">
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('settings.ignoredUsers.searchLabel')}
                  </label>
                  <div className="relative ignore-search-container">
                    <input
                      type="text"
                      placeholder={t('settings.ignoredUsers.searchPlaceholder')}
                      value={ignoreSearchQuery}
                      onChange={handleIgnoreSearchInputChange}
                      onFocus={handleIgnoreSearchInputFocus}
                      onBlur={handleIgnoreSearchInputBlur}
                      className="input w-full pr-10"
                    />
                    {isIgnoreSearching && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <LoadingSpinner size="small" />
                      </div>
                    )}
                    {!isIgnoreSearching && ignoreSearchQuery && (
                      <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    )}
                  </div>
                  
                  {/* Search Results Dropdown */}
                  {showIgnoreSearchResults && ignoreSearchResults.length > 0 && (
                    <div className="search-results-container absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg">
                      {ignoreSearchResults.map((result, index) => (
                        <div
                          key={index}
                          onClick={() => handleIgnoreSearchResultClick(result)}
                          onMouseDown={handleIgnoreSearchResultMouseDown}
                          className="flex items-center space-x-3 p-3 hover:bg-gray-700 cursor-pointer border-b border-gray-600 last:border-b-0 transition-colors"
                        >
                          {result.profileImage ? (
                            <img
                              src={result.profileImage}
                              alt={result.displayName}
                              className="w-8 h-8 rounded-full"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-twitch-600 flex items-center justify-center text-white font-medium">
                              {result.displayName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">{result.displayName}</p>
                            <p className="text-sm text-gray-400">@{result.username}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              result.type === 'database' ? 'bg-green-900 text-green-300' : 'bg-blue-900 text-blue-300'
                            }`}>
                              {result.type === 'database' ? t('settings.ignoredUsers.inDatabase') : t('settings.ignoredUsers.twitchUser')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder={t('settings.ignoredUsers.usernamePlaceholder')}
                    value={newIgnoreUsername}
                    onChange={(e) => setNewIgnoreUsername(e.target.value)}
                    className="input"
                    required
                    readOnly
                  />
                  <input
                    type="text"
                    placeholder="Reason (optional)"
                    value={newIgnoreReason}
                    onChange={(e) => setNewIgnoreReason(e.target.value)}
                    className="input"
                  />
                </div>
                <button type="submit" className="btn-primary w-full">
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('settings.ignoredUsers.addButton')}
                </button>
              </div>
            </form>

            {/* Ignored Users List */}
            <div className="space-y-3">
              {ignoredUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-600 light:bg-gray-300 flex items-center justify-center">
                        <span className="text-sm font-medium text-white light:text-gray-700">
                          {user.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="font-medium text-white light:text-gray-900">{user.username}</p>
                      {user.reason && (
                        <p className="text-sm text-gray-400">{user.reason}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => removeIgnoredUser(user.id)}
                    className="p-2 text-red-400 hover:text-red-300"
                    title="Remove from ignore list"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {ignoredUsers.length === 0 && (
                <div className="text-center text-gray-400 py-8">
                  <p>{t('settings.ignoredUsers.noIgnoredUsers')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Global Editors */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-6">{t('settings.globalEditors.title')}</h2>
            <p className="text-gray-400 light:text-gray-600 mb-4">
              {t('settings.globalEditors.description')}
            </p>

            {/* Add Global Editor Form */}
            <form onSubmit={addGlobalEditor} className="mb-6 p-4 bg-gray-700 rounded-lg">
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('settings.globalEditors.searchLabel')}
                  </label>
                  <div className="relative search-container">
                    <input
                      type="text"
                      placeholder={t('settings.globalEditors.searchPlaceholder')}
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      onFocus={handleSearchInputFocus}
                      onBlur={handleSearchInputBlur}
                      className="input w-full pr-10"
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <LoadingSpinner size="small" />
                      </div>
                    )}
                    {!isSearching && searchQuery && (
                      <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    )}
                  </div>
                  
                  {/* Search Results Dropdown */}
                  {showSearchResults && searchResults.length > 0 && (
                    <div className="search-results-container absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg">
                      {searchResults.map((result, index) => (
                        <div
                          key={index}
                          onClick={() => handleSearchResultClick(result)}
                          onMouseDown={handleSearchResultMouseDown}
                          className="flex items-center space-x-3 p-3 hover:bg-gray-700 cursor-pointer border-b border-gray-600 last:border-b-0 transition-colors"
                        >
                          {result.profileImage ? (
                            <img
                              src={result.profileImage}
                              alt={result.displayName}
                              className="w-8 h-8 rounded-full"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-twitch-600 flex items-center justify-center text-white font-medium">
                              {result.displayName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">{result.displayName}</p>
                            <p className="text-sm text-gray-400">@{result.username}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            {result.type === 'twitch' ? (
                              <button
                                onMouseDown={handleImportButtonMouseDown}
                                onClick={(e) => {
                                  e.stopPropagation();
                              
                                  handleSearchResultClick(result);
                                }}
                                className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                              >
                                <UserPlusIcon className="h-3 w-3 mr-1" />
                                {t('settings.globalEditors.importFromTwitch')}
                              </button>
                            ) : (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                result.status === 'Approved' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'
                              }`}>
                                {result.status}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-4">
                  <input
                    type="text"
                    placeholder={t('settings.globalEditors.usernamePlaceholder')}
                    value={newGlobalEditorUsername}
                    onChange={(e) => setNewGlobalEditorUsername(e.target.value)}
                    className="input flex-1"
                    required
                  />
                  <button type="submit" className="btn-primary">
                    <UserPlusIcon className="h-4 w-4 mr-2" />
                    {t('settings.globalEditors.inviteButton')}
                  </button>
                </div>
              </div>
            </form>

            {/* Current Global Editors */}
            <div className="space-y-3 mb-6">
              <h3 className="text-lg font-medium text-white light:text-gray-900">Current Global Editors</h3>
              {globalEditors.map((editor) => (
                <div
                  key={editor.id}
                  className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    {editor.editor.profileImage ? (
                      <img
                        src={editor.editor.profileImage}
                        alt={editor.editor.displayName}
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-twitch-600 flex items-center justify-center text-white font-medium">
                        {editor.editor.displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-white light:text-gray-900">{editor.editor.displayName}</p>
                      <p className="text-sm text-gray-400">@{editor.editor.username}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeGlobalEditor(editor.id)}
                    className="p-2 text-red-400 hover:text-red-300"
                    title="Remove global editor"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {globalEditors.length === 0 && (
                <div className="text-center text-gray-400 py-8">
                  <p>No global editors yet</p>
                </div>
              )}
            </div>

            {/* Received Invites */}
            {globalInvites.filter(invite => invite.status === 'pending' && invite.receiverId === user?.id).length > 0 && (
              <div className="space-y-3 mb-6">
                <h3 className="text-lg font-medium text-white light:text-gray-900">Invites Received</h3>
                {globalInvites
                  .filter(invite => invite.status === 'pending' && invite.receiverId === user?.id)
                  .map((invite) => (
                    <div
                      key={invite.id}
                      className="flex items-center justify-between p-3 bg-blue-900/20 border border-blue-600/30 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        {invite.sender.profileImage ? (
                          <img
                            src={invite.sender.profileImage}
                            alt={invite.sender.displayName}
                            className="w-8 h-8 rounded-full"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-twitch-600 flex items-center justify-center text-white font-medium">
                            {invite.sender.displayName.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-white light:text-gray-900">
                            {invite.sender.displayName} invited you to be an editor
                          </p>
                          <p className="text-sm text-gray-400">@{invite.sender.username}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => acceptGlobalEditorInvite(invite.id)}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => rejectGlobalEditorInvite(invite.id)}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md transition-colors"
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Sent Invites */}
            {globalInvites.filter(invite => invite.status === 'pending' && invite.senderId === user?.id).length > 0 && (
              <div className="space-y-3 mb-6">
                <h3 className="text-lg font-medium text-white light:text-gray-900">Invites Sent</h3>
                {globalInvites
                  .filter(invite => invite.status === 'pending' && invite.senderId === user?.id)
                  .map((invite) => (
                    <div
                      key={invite.id}
                      className="flex items-center justify-between p-3 bg-yellow-900/20 border border-yellow-600/30 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        {invite.receiver.profileImage ? (
                          <img
                            src={invite.receiver.profileImage}
                            alt={invite.receiver.displayName}
                            className="w-8 h-8 rounded-full"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-twitch-600 flex items-center justify-center text-white font-medium">
                            {invite.receiver.displayName.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-white light:text-gray-900">{invite.receiver.displayName}</p>
                          <p className="text-sm text-gray-400">@{invite.receiver.username} • Waiting for response</p>
                        </div>
                      </div>
                      <span className="text-sm text-yellow-400">Pending</span>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* OBS Integration */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-6">OBS Integration</h2>
            <p className="text-gray-400 light:text-gray-600 mb-4">
              Use these URLs in your OBS browser source to display greeter status on stream
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  General OBS URL
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={`${window.location.origin}/obs`}
                    readOnly
                    className="input flex-1 bg-gray-700"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/obs`);
                      toast.success(t('settings.copyUrlSuccess'));
                    }}
                    className="btn-secondary ml-2"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="text-sm text-gray-400">
                <p>• Width: 400px, Height: 600px recommended</p>
                <p>• The OBS overlay will automatically show your active session</p>
                <p>• Refresh rate: 2-3 seconds for best performance</p>
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="card p-6 border-2 border-red-600">
            <h2 className="text-xl font-semibold text-red-400 mb-6 flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 mr-2" />
              Danger Zone
            </h2>
            <p className="text-gray-400 light:text-gray-600 mb-6">
              These actions are permanent and cannot be undone. Please proceed with caution.
            </p>
            
            <div className="space-y-4">
              <div className="p-4 bg-red-900/20 border border-red-600 rounded-lg">
                <h3 className="text-lg font-medium text-red-400 mb-2">Delete Account</h3>
                <p className="text-sm text-gray-400 mb-4">
                  Permanently delete your account and all associated data including:
                </p>
                <ul className="text-sm text-gray-400 mb-4 list-disc list-inside space-y-1">
                  <li>All sessions and their chat history</li>
                  <li>All chatter records and greet status</li>
                  <li>Your user settings and preferences</li>
                  <li>Your ignored users list</li>
                </ul>
                <p className="text-sm text-red-400 mb-4 font-medium">
                  This action cannot be undone and you will need to reconnect with Twitch to use Waddup again.
                </p>
                <button
                  onClick={() => setDeleteAccountConfirm(true)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors"
                >
                  Delete My Account
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Account Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteAccountConfirm}
          onClose={() => setDeleteAccountConfirm(false)}
          onConfirm={handleDeleteAccount}
          title="Delete Account"
          message={`Are you sure you want to permanently delete your account "${user?.displayName}"? This action cannot be undone.`}
          confirmText="Delete My Account"
          cancelText="Cancel"
          type="danger"
          details={[
            'Your Twitch account will remain unchanged',
            'All Waddup sessions and chat data will be permanently deleted',
            'You will be logged out immediately',
            'You can reconnect with Twitch later to create a new Waddup account'
          ]}
        />
      </div>
      
      <Footer />
    </>
  );
};

export default SettingsPage; 